import { useIsAuthenticated } from '@azure/msal-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  useGetBatchSummaryByIdQuery,
  useListBatchSummariesQuery,
} from '~/api/generated-api';
import { useAppDispatch } from '~/app/store';
import {
  makeBatchUploadLocalStorageKey,
  useLocalStorage,
} from '~/common/hooks/useLocalStorage';
import { useOrgId } from '~/common/hooks/useOrgId';
import {
  BatchBoardingStatus,
  BatchBoardingUploadType,
} from '~/common/models/batchBoarding.model';
import {
  CustomEvents,
  dispatchCustomEvent,
} from '~/common/utils/custom-events';
import { refreshDashboardVehicleData } from '~/common/utils/dashboard-refresh.utils';
import { BatchUploadProgressWidget } from '~/features/assets/addVehicles/components/BatchUploadProgressWidget/BatchUploadProgressWidget';
import { setActiveBatchId } from '~/features/assets/assetsSlice';
import { selectActiveBatchIdByOrg } from '~/features/assets/assetsSlice.selectors';
import { selectActingProfile } from '~/store/profile/profileSlice.selectors';

const MIN_POLLING_INTERVAL_MS = 2000; // 2 seconds
const MAX_POLLING_INTERVAL_MS = 10000; // 10 seconds
const BACKOFF_MULTIPLIER = 1.5;

export const PendingWorkflowsSnackbar = React.memo(() => {
  const isAuthenticated = useIsAuthenticated();

  // Get the user's profile ID and active organization to determine active batches initiated by user.
  const profile = useSelector(selectActingProfile);
  const organizationsId = useOrgId();
  const fleetId = profile?.fleet?.id;

  // Track the active batch ID for polling
  const dispatch = useAppDispatch();
  const activeBatchId = useSelector(selectActiveBatchIdByOrg(organizationsId));

  // Track polling interval with exponential backoff
  const [pollingInterval, setPollingInterval] = useState(
    MIN_POLLING_INTERVAL_MS
  );
  const lastUpdatedDateRef = useRef<string | null>(null);

  // TODO: may want to update query to accept multiple statuses so we can get the active batch even if it is initializing
  // Step 1: Fetch batch summaries to find active batch
  const { currentData: batchSummariesData } = useListBatchSummariesQuery(
    {
      'cs-organization': organizationsId,
      ...(fleetId && { 'cs-fleet': fleetId }),
      createdBy: profile?.id || '',
      status: BatchBoardingStatus.IN_PROGRESS,
      size: 1, // You should only need the first active batch
    },
    // Hub and Fleet Id are optional, so we do not need to skip if they are not present
    {
      skip: !isAuthenticated || !organizationsId || !profile?.id,
    }
  );
  const activeBatchFromSummariesResponse =
    batchSummariesData?.result?.items?.[0];

  // Step 2: Fall back to local storage only if we don't have an active batch
  const { getItem, setItem, removeItem } = useLocalStorage();

  const pendingBatchLocalStorageKey = makeBatchUploadLocalStorageKey({
    profileId: profile?.id,
    organizationsId,
  });
  const batchIdFromStorage = getItem(pendingBatchLocalStorageKey);

  // Step 3: Set active batch ID from batch summaries or local storage so we can start polling or show progress widget
  useEffect(() => {
    // Determine active batch ID from batch summaries API response or local storage
    let batchId: string | null = null;

    // Auto onboarding batches never enter the widget through discovery:
    // multi-batch imports are tracked by the in-flight banner, and single-batch
    // imports are handed off explicitly via setActiveBatchId + local storage.
    if (
      activeBatchFromSummariesResponse?.id &&
      activeBatchFromSummariesResponse.status ===
        BatchBoardingStatus.IN_PROGRESS &&
      activeBatchFromSummariesResponse.uploadType !==
        BatchBoardingUploadType.AUTO_ONBOARDING
    ) {
      batchId = activeBatchFromSummariesResponse.id;
    } else if (!activeBatchId && batchIdFromStorage) {
      batchId = batchIdFromStorage;
    }

    if (!activeBatchId && batchId) {
      dispatch(setActiveBatchId({ organizationsId, batchId }));
      setPollingInterval(MIN_POLLING_INTERVAL_MS);
      lastUpdatedDateRef.current = null;
    }
  }, [
    activeBatchFromSummariesResponse,
    activeBatchId,
    batchIdFromStorage,
    dispatch,
    organizationsId,
  ]);

  // Step 4: Poll batch details if we have an active batch ID
  const { currentData: batchDetailsData } = useGetBatchSummaryByIdQuery(
    {
      'cs-organization': organizationsId,
      ...(fleetId && { 'cs-fleet': fleetId }),
      batchId: activeBatchId || '',
    },
    {
      // Stop polling if there is no active batch ID
      pollingInterval: activeBatchId ? pollingInterval : 0,

      // Skip the query altogether if no active batch ID
      skip: !activeBatchId,
    }
  );

  // Step 5: Stop polling when status is no longer IN_PROGRESS and handle exponential backoff
  useEffect(() => {
    if (batchDetailsData?.result) {
      const { id, status, updatedDate, uploadType } = batchDetailsData.result;
      // Batch can be in progress or initializing before the batch is in a terminal state
      const isBatchInProgress =
        status === BatchBoardingStatus.IN_PROGRESS ||
        status === BatchBoardingStatus.INITIALIZING;

      // Update local storage with current batch ID
      if (id && isBatchInProgress) {
        setItem(pendingBatchLocalStorageKey, id);

        // Implement exponential backoff if updatedDate hasn't changed
        if (updatedDate) {
          // Same updatedDate, increase polling interval
          if (lastUpdatedDateRef.current === updatedDate) {
            setPollingInterval((prevInterval) => {
              const newInterval = Math.min(
                prevInterval * BACKOFF_MULTIPLIER,
                MAX_POLLING_INTERVAL_MS
              );
              return Math.round(newInterval);
            });
          }

          // updatedDate changed, reset to minimum interval
          else {
            setPollingInterval(MIN_POLLING_INTERVAL_MS);
          }
          lastUpdatedDateRef.current = updatedDate;
        }
      }

      // Update the state of the widget when batch is no longer in progress
      if (!isBatchInProgress) {
        // Reset the polling interval and last updated date for new batches
        setPollingInterval(0);
        lastUpdatedDateRef.current = null;

        // Finally, reset the vehicles list view to reflect any changes.
        // This only matters when the user is on the vehicle list view and needs the page to refresh programmatically.
        dispatchCustomEvent(CustomEvents.BATCH_UPLOAD_COMPLETED_EVENT);

        if (uploadType === BatchBoardingUploadType.AUTO_ONBOARDING) {
          refreshDashboardVehicleData(dispatch);
        }
      }
    }
  }, [
    batchDetailsData?.result,
    dispatch,
    pendingBatchLocalStorageKey,
    setItem,
  ]);

  const pendingBatch = batchDetailsData?.result;

  const handleClose = () => {
    removeItem(pendingBatchLocalStorageKey);
    dispatch(setActiveBatchId({ organizationsId, batchId: null }));
  };

  if (
    // Missing necessary authentication and profile data
    !isAuthenticated ||
    !profile ||
    !organizationsId ||
    // There is no latest active batch (as perceived by the end user) to show
    !activeBatchId
  ) {
    return null;
  }

  return (
    <BatchUploadProgressWidget
      {...pendingBatch}
      organizationsId={organizationsId}
      fleetId={fleetId}
      onClose={handleClose}
    />
  );
});
